import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { PASS_CATEGORY_THUMBNAIL } from "@/components/myPage/passes/constants";
import SubscriptionBenefitDialog from "@/components/passes/dialogs/SubscriptionBenefitDialog";
import { cn } from "@/lib/utils";
import { dateKorFormatterOrNull, priceFormatter } from "@/lib/utils/formatter";
import { Button, Tag, Typography } from "@bookips/sds";
import Image from "next/image";
import { useState } from "react";
import { getSubscriptionCharge } from "src/api/subscription";
import { RadiusBoxStyle } from "..";
import ProductNoticeBox from "./ProductNoticeBox";
/** 월구독 주문상품 row의 안내 불릿 한 줄 */ const SubscriptionNoticeBullet = (param)=>/*#__PURE__*/ {
    let { children  } = param;
    return _jsxs("div", {
        className: "flex items-start gap-1.5",
        children: [
            /*#__PURE__*/ _jsx("span", {
                className: "mt-[7px] size-1 shrink-0 rounded-full bg-gray-500"
            }),
            /*#__PURE__*/ _jsx(Typography, {
                variant: "caption",
                className: "text-secondary flex-1",
                children: children
            })
        ]
    });
};
/** 월구독 주문상품 row의 금액 한 줄 ('이번 결제' / '다음 결제') */ const SubscriptionPriceRow = (param)=>{
    let { label , price , suffix  } = param;
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-wrap items-center gap-1",
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                variant: "body2",
                className: "text-secondary",
                children: "".concat(label, " :")
            }),
            /*#__PURE__*/ _jsx(Typography, {
                variant: "body1",
                className: "text-strong",
                children: "".concat(priceFormatter(price), "원")
            }),
            suffix && /*#__PURE__*/ _jsx(Typography, {
                variant: "body2",
                className: "text-secondary",
                children: "(".concat(suffix, ")")
            })
        ]
    });
};
/**
 * 월구독 아이템 row 레이아웃
 *
 * 다른 주문 타입과 달리 정가 취소선이 없고 '이번 결제 / 다음 결제' 2행 구조라 ItemRow를 쓰지 않는다.
 * plan 응답에 상품 이미지가 없어 썸네일은 기본 이용권 이미지를 쓴다.
 */ const SubscriptionItemRow = (param)=>{
    let { data  } = param;
    var _data_benefits_package, _data_currentPeriod, _data_currentPeriod1;
    const { recurringPrice , currentCharge  } = getSubscriptionCharge(data);
    const [isBenefitOpen, setIsBenefitOpen] = useState(false);
    /**
   * 이용권 정보 노출 방식이 sourceType 으로 갈린다.
   * - package: 구성 이용권이 여러 개라 개별 다이얼로그가 성립하지 않아 공통 유의사항을 인라인 박스로 보여준다
   * - membershipProduct: 단품이라 기존 이용권 다이얼로그와 같은 모양으로 띄운다
   */ const packageNotice = data.sourceType === "package" ? (_data_benefits_package = data.benefits.package) === null || _data_benefits_package === void 0 ? void 0 : _data_benefits_package.description : null;
    const benefitItem = data.sourceType === "package" ? undefined : data.benefits.items[0];
    // 날짜가 없으면 괄호 안내를 통째로 감춘다 (suffix 미지정 = 괄호 미노출).
    // dateKorFormatter 를 그대로 쓰면 누락값이 오늘 날짜로 둔갑하거나 'Invalid Date' 가 노출된다
    const periodStartsAt = dateKorFormatterOrNull((_data_currentPeriod = data.currentPeriod) === null || _data_currentPeriod === void 0 ? void 0 : _data_currentPeriod.startsAt);
    const periodEndsAt = dateKorFormatterOrNull((_data_currentPeriod1 = data.currentPeriod) === null || _data_currentPeriod1 === void 0 ? void 0 : _data_currentPeriod1.endsAt);
    // 한쪽만 찍으면 끊긴 기간이 되므로 둘 다 있을 때만 노출한다
    const currentPeriodText = periodStartsAt && periodEndsAt ? "".concat(periodStartsAt, " ~ ").concat(periodEndsAt) : null;
    const nextBillingText = dateKorFormatterOrNull(data.nextBillingDate);
    return /*#__PURE__*/ _jsxs(RadiusBoxStyle, {
        className: cn("flex flex-col gap-6", "max-sm:rounded-none max-sm:border-none max-sm:p-5"),
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                variant: "h6",
                className: "flex items-center gap-2",
                children: "주문상품"
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-col gap-5",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-start gap-5",
                        children: [
                            /*#__PURE__*/ _jsx("p", {
                                className: "relative size-[68px] shrink-0 overflow-hidden rounded-md border border-gray-300/20",
                                children: /*#__PURE__*/ _jsx(Image, {
                                    src: PASS_CATEGORY_THUMBNAIL["freepass"],
                                    alt: "",
                                    fill: true
                                })
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex flex-1 flex-col gap-3",
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "flex items-start gap-1.5",
                                        children: /*#__PURE__*/ _jsxs(Typography, {
                                            variant: "body1",
                                            className: "whitespace-break-spaces",
                                            children: [
                                                /*#__PURE__*/ _jsx(Tag, {
                                                    color: "indigo",
                                                    size: "sm",
                                                    bold: true,
                                                    className: "mr-1.5",
                                                    children: "월 구독"
                                                }),
                                                data.title
                                            ]
                                        })
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex flex-col gap-1",
                                        children: [
                                            /*#__PURE__*/ _jsx(SubscriptionPriceRow, {
                                                label: "이번 결제",
                                                price: currentCharge,
                                                // 체험은 무료 기간을, 유료는 이번 회차 이용 기간을 괄호에 넣는다.
                                                // currentPeriod 는 만료 산정 규칙상 FE 계산 금지라 서버값을 그대로 쓴다.
                                                suffix: data.trialApplied ? "".concat(data.trial.days, "일 무료") : currentPeriodText !== null && currentPeriodText !== void 0 ? currentPeriodText : undefined
                                            }),
                                            /*#__PURE__*/ _jsx(SubscriptionPriceRow, {
                                                label: "다음 결제",
                                                price: recurringPrice,
                                                suffix: nextBillingText ? "".concat(nextBillingText, " 결제 예정") : undefined
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex flex-col gap-1",
                                        children: [
                                            !data.trialApplied && /*#__PURE__*/ _jsx(SubscriptionNoticeBullet, {
                                                children: "재구독 시에는 무료 체험 혜택 및 첫 구독 할인 가격이 적용되지 않습니다."
                                            }),
                                            /*#__PURE__*/ _jsx(SubscriptionNoticeBullet, {
                                                children: "매달 자동으로 구독료 청구 및 결제가 진행됩니다."
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    }),
                    packageNotice && /*#__PURE__*/ _jsx(ProductNoticeBox, {
                        description: packageNotice
                    }),
                    benefitItem && /*#__PURE__*/ _jsx(Button, {
                        variant: "stroke",
                        color: "secondary",
                        size: "lg",
                        fullWidth: true,
                        onClick: ()=>setIsBenefitOpen(true),
                        children: "이용권 정보 보기"
                    })
                ]
            }),
            isBenefitOpen && benefitItem && /*#__PURE__*/ _jsx(SubscriptionBenefitDialog, {
                item: benefitItem,
                onClose: ()=>setIsBenefitOpen(false)
            })
        ]
    });
};
export default SubscriptionItemRow;
